import {
  Fragment,
  createContext,
  useContext,
  useState,
  type FC,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useMatches } from '@tanstack/react-router';
import { ChevronRight } from 'lucide-react';

/*
 * Крошки объявляет сам маршрут: `staticData: { crumbs: ['Мои задачи'] }`.
 * Так название экрана лежит рядом с маршрутом, а не пробрасывается снизу
 * вверх через контекст или состояние.
 */
declare module '@tanstack/react-router' {
  interface StaticDataRouteOption {
    crumbs?: ReadonlyArray<string>;
  }
}

/*
 * Действия экрана («Добавить отгрузку», «Редактировать») стоят справа в топбаре,
 * как в макете, но принадлежат экрану: у него их состояние и обработчики. Экран
 * рендерит <TopbarActions>, а тот порталом переносит кнопки в слот топбара.
 * Слот - элемент, который топбар отдаёт в контекст layout-а.
 */
const TopbarSlotContext = createContext<{
  slot: HTMLElement | null;
  setSlot: (element: HTMLElement | null) => void;
} | null>(null);

/** Держит слот действий топбара; оборачивает layout рабочего места. */
export const TopbarSlotProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [slot, setSlot] = useState<HTMLElement | null>(null);

  return (
    <TopbarSlotContext.Provider value={{ slot, setSlot }}>
      {children}
    </TopbarSlotContext.Provider>
  );
};

/** Кнопки экрана в правой части топбара. */
export const TopbarActions: FC<{ children: ReactNode }> = ({ children }) => {
  const slot = useContext(TopbarSlotContext)?.slot;

  return slot ? createPortal(children, slot) : null;
};

/*
 * Верхняя строка рабочего места. Повторяет .topbar макета: белая полоса 56px
 * с хлебной крошкой, последняя крошка выделена, справа - действия экрана.
 */
export const Topbar: FC = () => {
  const matches = useMatches();
  const setSlot = useContext(TopbarSlotContext)?.setSlot;
  const crumbs =
    [...matches].reverse().find((match) => match.staticData.crumbs)?.staticData
      .crumbs ?? [];

  if (crumbs.length === 0) return null;

  return (
    <div className="flex h-14 min-h-14 items-center gap-4 border-b border-ink-200 bg-white px-6">
      <div className="flex items-center gap-1.5 text-[13px] whitespace-nowrap text-ink-500">
        {crumbs.map((crumb, index) => (
          <Fragment key={crumb}>
            {index > 0 && (
              <ChevronRight aria-hidden className="size-[13px] text-ink-400" />
            )}
            {index === crumbs.length - 1 ? (
              <span className="font-semibold text-ink-900">{crumb}</span>
            ) : (
              <span>{crumb}</span>
            )}
          </Fragment>
        ))}
      </div>
      <div ref={setSlot} className="ml-auto flex items-center gap-2" />
    </div>
  );
};
